import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useToast } from "@/components/ui/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { confirm } from "@/components/ui/confirm";
import { ConfirmDialog } from "@/components/ui/confirm";
import CompanyWebSearchPanel from '@/components/CompanyWebSearchPanel';
import { useAuth } from '@/context/AuthContext';
import { getLeadsByCompanyId } from '@/services/leads';
import { getCompanyById, deleteCompany } from '@/services/companyService';

// Import our components
import CompanyDetailsHeader from '@/components/companies/CompanyDetailsHeader';
import CompanyDetailsInfo from '@/components/companies/CompanyDetailsInfo';
import CompanyQuickActions from '@/components/companies/CompanyQuickActions';
import CompanyLeadsTable from '@/components/companies/CompanyLeadsTable';
import CompanyLoadingState from '@/components/companies/CompanyLoadingState';
import CompanyNotFound from '@/components/companies/CompanyNotFound';
import CompanyMembersTab from '@/components/companies/CompanyMembersTab';

const CompanyDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { profile, activeCompany, setActiveCompany } = useAuth();
  const [company, setCompany] = useState(null);
  const [companyLeads, setCompanyLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const isAdmin = profile?.role === 'admin';
  const isActiveCompany = activeCompany?.id === id;

  // Fetch company and leads in parallel
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setIsLoadingLeads(true);
        
        // Fetch company and leads in parallel for better performance
        const [companyData, leadsData] = await Promise.all([
          getCompanyById(id),
          getLeadsByCompanyId(id)
        ]);
        
        setCompany(companyData);
        setCompanyLeads(leadsData);
      } catch (error) {
        console.error('Error fetching company data:', error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load company details"
        });
      } finally {
        setIsLoading(false);
        setIsLoadingLeads(false);
      }
    };

    fetchCompanyData();
  }, [id, toast]);

  const handleDelete = async () => {
    if (!company) return;
    
    if (!isAdmin) {
      toast({
        variant: "destructive",
        title: "Permission Denied",
        description: "Only administrators can delete companies."
      });
      return;
    }
    
    confirm({
      title: "Delete Company",
      description: `Are you sure you want to delete ${company.name}? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await deleteCompany(company.id);
          toast({
            title: "Success",
            description: `${company.name} has been deleted successfully.`,
          });
          navigate('/companies');
        } catch (error) {
          console.error("Error deleting company:", error);
          toast({
            title: "Error",
            description: "Failed to delete the company. Please try again.",
            variant: "destructive",
          });
        }
      },
    });
  };

  const handleSetActiveCompany = async () => {
    if (!company) return;
    
    try {
      const success = await setActiveCompany(company.id);
      if (success) {
        toast({
          title: "Success",
          description: `You are now working in ${company.name}.`
        });
      }
    } catch (error) {
      console.error("Error setting active company:", error);
      toast({
        title: "Error",
        description: "Failed to set active company.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto p-4 md:p-6">
          <CompanyLoadingState />
        </main>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto p-4 md:p-6">
          <CompanyNotFound />
        </main>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 md:p-6 lg:p-8">
      <ConfirmDialog />
      
      <Card className="bg-white shadow-md border-gray-200 mb-6">
        <CardContent className="p-4 sm:p-5 md:p-6">
          <CompanyDetailsHeader 
            company={company}
            isAdmin={isAdmin}
            isActiveCompany={isActiveCompany}
            onDelete={handleDelete}
          />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
        <div className="lg:col-span-2">
          <Card className="bg-white shadow-md border-gray-200">
            <Tabs defaultValue="details">
              <div className="border-b bg-gray-50/50 p-3 sm:p-4">
                <TabsList className="bg-white shadow-sm w-full sm:w-auto overflow-x-auto scrollbar-hide">
                  <TabsTrigger value="details" className="whitespace-nowrap">Details</TabsTrigger>
                  <TabsTrigger value="leads" className="whitespace-nowrap">Leads</TabsTrigger>
                  <TabsTrigger value="members" className="whitespace-nowrap">Members</TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="details" className="p-4 sm:p-5 md:p-6">
                <CompanyDetailsInfo company={company} />
                
                <div className="mt-4 sm:mt-6 p-4 sm:p-5 md:p-6 bg-gray-50 rounded-lg">
                  <CompanyWebSearchPanel 
                    key={company.id}
                    company={company} 
                    onRefresh={() => {
                      // Optionally refresh company data
                    }} 
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="leads" className="p-4 sm:p-5 md:p-6">
                <CompanyLeadsTable 
                  companyLeads={companyLeads}
                  isLoadingLeads={isLoadingLeads}
                />
              </TabsContent>
              
              <TabsContent value="members" className="p-4 sm:p-5 md:p-6">
                <CompanyMembersTab 
                  companyId={company.id}
                  isAdmin={isAdmin}
                />
              </TabsContent>
            </Tabs>
          </Card>
        </div>
        
        <div>
          <Card className="bg-white shadow-md border-gray-200 sticky top-24">
            <CardContent className="p-6">
              <CompanyQuickActions
                company={company}
                isAdmin={isAdmin}
                isActiveCompany={isActiveCompany}
                onSetActiveCompany={handleSetActiveCompany}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetails;
